/**
 * Hook for fetching survey data for export report
 */

import { useQuery } from '@tanstack/react-query'
import axios, { pluckData } from 'src/utilities/http-client'

/**
 * Survey response item from API
 */
export interface SurveyDataItem {
  id: string
  userID: string
  content: string // JSON string containing survey responses
  createdTime: string
  updatedTime: string
  name: string // User name
}

/**
 * Parsed survey item for DataGrid
 */
export interface ParsedSurveyItem extends Record<string, any> {
  id: string
  name: string
  date: string
}

/**
 * Query key factory for survey data
 */
export let surveyQueryKeys = {
  all: ['v4-dashboard-surveys'] as const,
  byService: (linkedServiceID: string) =>
    [...surveyQueryKeys.all, 'service', linkedServiceID] as const,
}

/**
 * Hook to fetch survey data for a specific linked service
 */
export function useSurveyData(linkedServiceID: string, enabled = true) {
  return useQuery({
    queryKey: surveyQueryKeys.byService(linkedServiceID),
    queryFn: async (): Promise<ParsedSurveyItem[]> => {
      if (!linkedServiceID) {
        return []
      }

      let url = `/api/manage/dashboard/surveys/linked-service/${encodeURIComponent(linkedServiceID)}`
      let response = await axios.get<SurveyDataItem[]>(url).then(pluckData)

      // Parse survey content and flatten to rows
      return response.map((survey) => {
        try {
          let surveyContent = JSON.parse(survey.content)
          return {
            id: survey.id,
            name: survey.name,
            date: survey.createdTime,
            ...surveyContent,
          }
        } catch {
          return {
            id: survey.id,
            name: survey.name,
            date: survey.createdTime,
          }
        }
      })
    },
    enabled: enabled && Boolean(linkedServiceID),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
